import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { NoteForm } from "./notes/NoteForm";
import { NoteItem } from "./notes/NoteItem";

interface ClientNotesProps {
  clientId: number;
}

interface Note {
  id: string;
  note: string;
  created_at: string;
  is_response: boolean;
}

export const ClientNotes = ({ clientId }: ClientNotesProps) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editedNoteContent, setEditedNoteContent] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchNotes();
  }, [clientId]);

  const fetchNotes = async () => {
    const { data, error } = await supabase
      .from("client_notes")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch notes",
      });
      return;
    }

    setNotes(data || []);
  };

  const addNote = async (isResponse: boolean = false) => {
    if (!newNote.trim()) return;

    const { error } = await supabase.from("client_notes").insert([
      {
        client_id: clientId,
        note: newNote,
        is_response: isResponse,
      },
    ]);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add note",
      });
      return;
    }

    setNewNote("");
    fetchNotes();
    
    toast({
      title: "Success",
      description: "Note added successfully",
    });
  };

  const startEditing = (note: Note) => {
    setEditingNoteId(note.id);
    setEditedNoteContent(note.note);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditedNoteContent("");
  };

  const saveEdit = async (noteId: string) => {
    if (!editedNoteContent.trim()) return;

    const { error } = await supabase
      .from("client_notes")
      .update({ note: editedNoteContent })
      .eq("id", noteId);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update note",
      });
      return;
    }

    setEditingNoteId(null);
    fetchNotes();
    
    toast({
      title: "Success",
      description: "Note updated successfully",
    });
  };

  const deleteNote = async (noteId: string) => {
    const { error } = await supabase
      .from("client_notes")
      .delete()
      .eq("id", noteId);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete note",
      });
      return;
    }

    fetchNotes();
    
    toast({
      title: "Success",
      description: "Note deleted successfully",
    });
  };

  return (
    <Card className="p-6 bg-[#222222] border-white/10">
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Notes & Responses</h3>
          <NoteForm
            value={newNote}
            onChange={setNewNote}
            onSubmit={addNote}
          />
        </div>

        <div className="space-y-4">
          {notes.map((note) => (
            <NoteItem
              key={note.id}
              note={note}
              isEditing={editingNoteId === note.id}
              editedContent={editedNoteContent}
              onEdit={() => startEditing(note)}
              onSave={() => saveEdit(note.id)}
              onCancel={cancelEditing}
              onDelete={() => deleteNote(note.id)}
              onEditChange={setEditedNoteContent}
            />
          ))}
        </div>
      </div>
    </Card>
  );
};